import { useCallback, useEffect, useRef, useState } from 'react';

export type CountdownStatus = 'wacht' | 'loopt' | 'gestopt' | 'klaar';

/** A visible countdown that starts once `active` becomes true (e.g. when scrolled into view). */
export function useCountdown(durationMs: number, active: boolean) {
  const [status, setStatus] = useState<CountdownStatus>('wacht');
  const [remaining, setRemaining] = useState(durationMs);
  const startRef = useRef(0);

  const restart = useCallback(() => {
    startRef.current = performance.now();
    setRemaining(durationMs);
    setStatus('loopt');
  }, [durationMs]);

  useEffect(() => {
    if (active && status === 'wacht') restart();
  }, [active, status, restart]);

  useEffect(() => {
    if (status !== 'loopt') return;
    let raf = 0;
    const tick = () => {
      const left = Math.max(0, durationMs - (performance.now() - startRef.current));
      setRemaining(left);
      if (left <= 0) {
        setStatus('klaar');
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [status, durationMs]);

  const stop = useCallback(() => setStatus('gestopt'), []);

  return { remaining, status, stop, restart };
}
